import { ActionIcon, Combobox, Group, Loader, Stack, Switch, Text, TextInput } from '@mantine/core';

import { Icon } from '@/components/Icon';
import type { ClusterOrganizationEntry, OrganizationFormData } from './useOrganizationForm';
import type { useComboboxSearch } from './useComboboxSearch';
import styles from './EditOrganizationModal.module.sass';

const LAYOUT = {
  SEARCH_ICON_SIZE: 16,
  REMOVE_ICON_SIZE: 16,
  DROPDOWN_HEIGHT: 200,
};

type MainChurchDisplay = {
  name: string;
  city: string;
  state: string;
  zipCode: string;
};

const getMainChurchDisplay = ({
  formData,
}: {
  formData: OrganizationFormData;
}): MainChurchDisplay => ({
  name: formData.fullName,
  city: formData.city,
  state: formData.state,
  zipCode: formData.zipCode,
});

type OrganizationClusterSectionProps = {
  formData: OrganizationFormData;
  isClusterEditable: boolean;
  clusterSearch: ReturnType<
    typeof useComboboxSearch<unknown, { value: string; label: string; city: string; zipCode: string }>
  >;
  onInputChange: (field: keyof OrganizationFormData, value: string | boolean | ClusterOrganizationEntry[]) => void;
  onRemoveClusterOrganization: (id: string) => void;
  onClusterSelection: (value: string | null) => void;
  onClusterProductsChange: (id: string, productsEnabled: boolean) => void;
};

export const OrganizationClusterSection = ({
  formData,
  isClusterEditable,
  clusterSearch,
  onInputChange,
  onRemoveClusterOrganization,
  onClusterSelection,
  onClusterProductsChange,
}: OrganizationClusterSectionProps) => {
  const mainChurch = getMainChurchDisplay({ formData });
  const hasMainChurchAddress = !!(mainChurch.city || mainChurch.state || mainChurch.zipCode);

  return (
    <>
      <Group justify="space-between">
        <Text fw={600}>Cluster</Text>
        <Switch
          checked={formData.clusterEnabled}
          onChange={(event) => {
            onInputChange('clusterEnabled', event.currentTarget.checked);
            if (event.currentTarget.checked) {
              onInputChange('clusterName', '');
              onInputChange('clusterOrganizations', []);
            }
          }}
          disabled={!isClusterEditable}
        />
      </Group>

      {formData.clusterEnabled && (
        <Stack gap="md">
          <TextInput
            label="Name"
            placeholder="Cluster name"
            value={formData.clusterName}
            onChange={(e) => onInputChange('clusterName', e.target.value)}
          />

          <Group justify="space-between" align="flex-start">
            <Text fw={600}>Main</Text>
            <Stack gap="0" align="flex-end">
              <Text size="sm">{mainChurch.name}</Text>
              {hasMainChurchAddress && (
                <Text size="sm">
                  {mainChurch.city}
                  {mainChurch.state && `, ${mainChurch.state}`}
                  {mainChurch.zipCode && `, ${mainChurch.zipCode}`}
                </Text>
              )}
            </Stack>
          </Group>

          {formData.clusterOrganizations.map((org, index) => (
            <Group
              key={org.id}
              className={styles.clusterOrganizationRow}
              wrap="nowrap"
              align="center"
              gap="xs"
            >
              <Text size="sm" className={styles.clusterOrganizationName}>
                {index + 1}. {org.name}
                {org.city && ` - ${org.city}`}
                {org.zipCode && `, ${org.zipCode}`}
              </Text>
              <Group gap="xs" wrap="nowrap" className={styles.clusterProductsSwitch}>
                <Text size="sm">Individual App</Text>
                <Switch
                  size="sm"
                  aria-label={`Individual App enabled for ${org.name}`}
                  checked={org.productsEnabled}
                  onChange={(event) => onClusterProductsChange(org.id, event.currentTarget.checked)}
                />
              </Group>
              <ActionIcon
                variant="subtle"
                className={styles.clusterOrganizationRemove}
                onClick={() => onRemoveClusterOrganization(org.id)}
                color="red"
                aria-label="Remove church from cluster"
              >
                <Icon name="IconX" size={LAYOUT.REMOVE_ICON_SIZE} />
              </ActionIcon>
            </Group>
          ))}

          {formData.clusterOrganizations.length < 1 && (
            <Text size="xs" c="red">
              There should be at least one church at cluster
            </Text>
          )}

          <Combobox store={clusterSearch.combobox} onOptionSubmit={onClusterSelection} withinPortal={false}>
            <Combobox.Target>
              <TextInput
                placeholder="Type at least 3 characters to search churches"
                value={clusterSearch.searchTerm}
                onChange={(event) => clusterSearch.handleSearch(event.currentTarget.value)}
                onClick={() => clusterSearch.combobox.openDropdown()}
                onBlur={() => clusterSearch.combobox.closeDropdown()}
                leftSection={<Icon name="IconSearch" size={LAYOUT.SEARCH_ICON_SIZE} />}
                rightSection={clusterSearch.isLoading ? <Loader size="xs" /> : null}
                disabled={clusterSearch.isLoading}
              />
            </Combobox.Target>

            <Combobox.Dropdown hidden={clusterSearch.results.length === 0 && !clusterSearch.isLoading}>
              <Combobox.Options mah={LAYOUT.DROPDOWN_HEIGHT} className={styles.dropdownOptions}>
                {clusterSearch.results.map((item) => (
                  <Combobox.Option value={item.value} key={item.value}>
                    {item.label} - {item.city}
                    {item.zipCode ? `, ${item.zipCode}` : ''}
                  </Combobox.Option>
                ))}
                {clusterSearch.isEmpty && <Combobox.Empty>No churches found</Combobox.Empty>}
              </Combobox.Options>
            </Combobox.Dropdown>
          </Combobox>
        </Stack>
      )}
    </>
  );
};
